<template>
  <form @submit.prevent="update" class="space-y-6">
    <!-- Basic Info -->
    <div class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Tag Informatie</h3>

      <div class="space-y-4">
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="name">
            Tag Naam
          </label>
          <input v-model="data.name"
                 class="p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200"
                 type="text"
                 id="name"
                 required>
        </div>

        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="target_url">
            Redirect URL
          </label>
          <input v-model="data.target_url"
                 class="p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200"
                 type="url"
                 id="target_url"
                 placeholder="https://example.com"
                 required>
        </div>
      </div>
    </div>

    <!-- QR Code Styling -->
    <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">QR Code Stijl</h3>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Size -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="qr_size">
            Grootte: {{ data.qr_size }}px
          </label>
          <input v-model="data.qr_size"
                 type="range"
                 min="100"
                 max="1000"
                 step="50"
                 id="qr_size"
                 class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
          <div class="flex justify-between text-xs text-gray-600 mt-1">
            <span>100px</span>
            <span>1000px</span>
          </div>
        </div>

        <!-- Style -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="qr_style">
            Blok Stijl
          </label>
          <select v-model="data.qr_style"
                  id="qr_style"
                  class="p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200">
            <option value="square">Vierkant</option>
            <option value="dot">Dots</option>
            <option value="round">Rond</option>
          </select>
        </div>

        <!-- Eye Style -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="qr_eye_style">
            Hoek Stijl
          </label>
          <select v-model="data.qr_eye_style"
                  id="qr_eye_style"
                  class="p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200">
            <option value="square">Vierkant</option>
            <option value="circle">Cirkel</option>
          </select>
        </div>

        <!-- Color -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="qr_color">
            Voorgrond Kleur
          </label>
          <div class="flex items-center space-x-2">
            <input v-model="colorHex"
                   type="color"
                   id="qr_color"
                   class="h-12 w-20 rounded border border-gray-300 cursor-pointer">
            <input v-model="colorHex"
                   type="text"
                   class="flex-1 p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 focus:border-blue-500 focus:ring focus:ring-blue-200"
                   placeholder="#000000">
          </div>
        </div>

        <!-- Background Color -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="qr_bg_color">
            Achtergrond Kleur
          </label>
          <div class="flex items-center space-x-2">
            <input v-model="bgColorHex"
                   type="color"
                   id="qr_bg_color"
                   class="h-12 w-20 rounded border border-gray-300 cursor-pointer">
            <input v-model="bgColorHex"
                   type="text"
                   class="flex-1 p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 focus:border-blue-500 focus:ring focus:ring-blue-200"
                   placeholder="#FFFFFF">
          </div>
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="errors" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <div v-if="errors.errors" class="text-red-700 text-sm space-y-1">
        <div v-for="(error, field) in errors.errors" :key="field">
          {{ error[0] }}
        </div>
      </div>
      <div v-else-if="errors.message" class="text-red-700 text-sm">
        {{ errors.message }}
      </div>
    </div>

    <!-- Submit Button -->
    <div class="flex items-center justify-end space-x-3">
      <router-link :to="{ name: 'Tags' }"
                   class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md font-semibold text-sm transition">
        Annuleren
      </router-link>
      <button type="submit"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold text-sm transition disabled:opacity-50">
        {{ loading ? 'Bezig...' : 'Tag Aanmaken' }}
      </button>
    </div>
  </form>
</template>

<script>
import { mapActions, mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
      errors: null,
      loading: false,
      colorHex: '#000000',
      bgColorHex: '#FFFFFF',
      data: {
        name: null,
        type: 'redirect',
        target_url: null,
        qr_size: 500,
        qr_style: 'square',
        qr_eye_style: 'square',
        qr_color: '0,0,0',
        qr_bg_color: '255,255,255',
      }
    }
  },
  computed: {
    ...mapStores(useAuthStore),
    user() {
      return this.authStore.currentUser
    },
  },
  watch: {
    colorHex(newVal) {
      this.data.qr_color = this.hexToRgb(newVal)
    },
    bgColorHex(newVal) {
      this.data.qr_bg_color = this.hexToRgb(newVal)
    }
  },
  methods: {
    ...mapActions(useAuthStore, ['attempt_user']),
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`
        : '0,0,0';
    },
    update() {
      this.errors = null
      this.loading = true

      axios.post('/api/tags', this.data)
        .then((response) => {
          this.attempt_user()
          this.loading = false
          this.$router.push({ name: 'Tags' })

          setTimeout(() => {
            toast.success('Tag aangemaakt!', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }, 50);
        })
        .catch((error) => {
          this.errors = error.response?.data || { message: 'Er is een fout opgetreden.' }
          this.loading = false
        })
    },
  }
}
</script>
